"use client";

import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { subscribeIncomingCalls, isCallExpired, markCallMissed, declineCall, answerCall, type CallDoc } from '@/services/calls';
import { getRingtonePrefs, startRingtone, stopRingtone } from '@/services/ringtone';
import { db } from '@/lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Phone, PhoneOff } from 'lucide-react';

type CallMode = 'ring' | 'silent' | 'flash' | 'ring+flash';

interface IncomingCallState {
  call: CallDoc;
  callMode: CallMode;
}

export function GlobalCallOverlay() {
  const { user } = useAuth();
  const [incomingCall, setIncomingCall] = useState<IncomingCallState | null>(null);
  const [callDuration, setCallDuration] = useState(0);

  // Use refs so the subscription callback never goes stale and
  // never needs to be re-created when incomingCall state changes.
  const incomingCallRef = useRef<IncomingCallState | null>(null);
  const expiryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const durationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const flashIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  // Load user's call mode preference
  const loadCallMode = useCallback(async (userId: string): Promise<CallMode> => {
    try {
      const userProfileDoc = await getDoc(doc(db, 'userProfiles', userId));
      const mode = userProfileDoc.data()?.callMode as CallMode;
      return mode || 'ring';
    } catch {
      return 'ring';
    }
  }, []);

  const startFlashAnimation = useCallback(() => {
    let isFlashed = false;
    flashIntervalRef.current = setInterval(() => {
      const html = document.documentElement;
      if (isFlashed) {
        html.style.borderColor = '';
        html.style.borderWidth = '';
      } else {
        html.style.borderColor = '#22c55e';
        html.style.borderWidth = '4px';
      }
      isFlashed = !isFlashed;
    }, 500);
  }, []);

  const stopFlashAnimation = useCallback(() => {
    if (flashIntervalRef.current) clearInterval(flashIntervalRef.current);
    const html = document.documentElement;
    html.style.borderColor = '';
    html.style.borderWidth = '';
  }, []);

  const clearTimers = useCallback(() => {
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    if (durationTimerRef.current) clearInterval(durationTimerRef.current);
    if (flashIntervalRef.current) clearInterval(flashIntervalRef.current);
  }, []);

  const dismissCall = useCallback(() => {
    clearTimers();
    stopRingtone();
    stopFlashAnimation();
    setIncomingCall(null);
    incomingCallRef.current = null;
    setCallDuration(0);
  }, [clearTimers, stopFlashAnimation]);

  const handleDecline = useCallback(async () => {
    const current = incomingCallRef.current;
    if (!current || !user?.uid) return;
    try {
      await declineCall(current.call.id, user.uid);
    } catch (err) {
      console.error('Error declining call:', err);
    } finally {
      dismissCall();
    }
  }, [user?.uid, dismissCall]);

  const handleAnswer = useCallback(async () => {
    const current = incomingCallRef.current;
    if (!current || !user?.uid) return;
    try {
      // CRITICAL: set sessionStorage BEFORE answerCall updates Firestore status → active.
      // app-shell's subscribeActiveCallForUser fires the moment status becomes 'active'
      // and checks sessionStorage. If it's not set yet, it incorrectly treats the call
      // as stale and immediately ends it (race condition → call drops on answer).
      sessionStorage.setItem('bb-active-call-id', current.call.id);
      await answerCall(current.call.id);
    } catch (err) {
      console.error('Error answering call:', err);
      // Roll back if answerCall failed so we don't leave a ghost entry
      sessionStorage.removeItem('bb-active-call-id');
    } finally {
      // Let the subscription clean up the overlay once status → active
      dismissCall();
    }
  }, [user?.uid, dismissCall]);

  // Subscribe to incoming calls.
  // NOTE: incomingCall is intentionally NOT in the dependency array.
  // We use incomingCallRef to read current state inside the callback
  // so this effect only ever runs once per user session, preventing
  // the subscription from being torn down (and stopRingtone called)
  // every time a call arrives.
  useEffect(() => {
    if (!user?.uid) return;
    const userId = user.uid;

    const unsub = subscribeIncomingCalls(userId, async (calls) => {
      // Mark expired calls as missed
      for (const call of calls) {
        if (isCallExpired(call)) {
          await markCallMissed(call.id);
        }
      }

      const activeCall = calls.find(c => !isCallExpired(c));
      const current = incomingCallRef.current;

      if (activeCall && !current) {
        // New incoming call — start everything
        const callMode = await loadCallMode(userId);
        const newState: IncomingCallState = { call: activeCall, callMode };
        setIncomingCall(newState);
        incomingCallRef.current = newState;
        setCallDuration(0);

        // Start ringtone (ring or ring+flash)
        if (callMode === 'ring' || callMode === 'ring+flash') {
          const ringtonePrefs = await getRingtonePrefs(userId);
          startRingtone(ringtonePrefs);
        }

        // Flash animation (flash or ring+flash)
        if (callMode === 'flash' || callMode === 'ring+flash') {
          startFlashAnimation();
        }

        // Auto-expire after 30 seconds
        expiryTimerRef.current = setTimeout(async () => {
          const c = incomingCallRef.current;
          if (c) {
            await markCallMissed(c.call.id);
            dismissCall();
          }
        }, 30000);

        // Duration counter
        durationTimerRef.current = setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);

      } else if (!activeCall && current) {
        // Call ended / was answered elsewhere
        dismissCall();
      }
    });

    return () => {
      unsub();
      clearTimers();
      stopRingtone();
      stopFlashAnimation();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  if (!incomingCall) return null;

  const call = incomingCall.call;
  const callMode = incomingCall.callMode;

  // Flash mode: minimal banner at top
  if (callMode === 'flash') {
    return (
      <div className="fixed top-[var(--header-height)] left-0 right-0 z-[70] bg-amber-500/80 text-white px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarImage src={call.callerAvatar} alt={call.callerName} />
            <AvatarFallback name={call.callerName}>{call.callerName[0]}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{call.callerName}</p>
            <p className="text-xs opacity-90">{call.type === 'video' ? 'Video call' : 'Audio call'}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button
            size="sm"
            onClick={handleAnswer}
            className="bg-green-600 text-white hover:bg-green-700 h-8 px-3"
          >
            <Phone className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDecline}
            className="text-white hover:bg-white/20 h-8 px-3"
          >
            <PhoneOff className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Ring / Silent mode: full-screen overlay (FaceTime / WhatsApp style)
  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-gradient-to-b from-gray-900 via-gray-900 to-black">
      {/* Caller info — centered in the top 2/3 */}
      <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8">
        {/* Avatar with pulsing rings */}
        <div className="relative flex items-center justify-center">
          {callMode === 'ring' && (
            <>
              <span className="absolute h-48 w-48 rounded-2xl bg-white/5 animate-ping" style={{ animationDuration: '2.5s' }} />
              <span className="absolute h-40 w-40 rounded-2xl bg-white/8 animate-ping" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }} />
            </>
          )}
          <Avatar className="h-32 w-32 rounded-2xl shadow-2xl z-10">
            <AvatarImage src={call.callerAvatar} alt={call.callerName} />
            <AvatarFallback className="text-5xl font-bold rounded-2xl bg-gray-700 text-white">
              {call.callerName[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </div>

        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold text-white tracking-tight">{call.callerName}</h2>
          <p className="text-base text-white/60">
            {call.type === 'video' ? 'Video call' : 'Audio call'}
          </p>
          <p className="text-sm text-white/40 font-mono">{callDuration}s</p>
        </div>
      </div>

      {/* Buttons — bottom third */}
      <div className="pb-16 px-12 flex items-center justify-around">
        {/* Decline */}
        <div className="flex flex-col items-center gap-2">
          <button
            onClick={handleDecline}
            className="h-18 w-18 h-[72px] w-[72px] bg-red-600 hover:bg-red-500 active:bg-red-700 text-white rounded-full flex items-center justify-center shadow-lg transition-colors"
            aria-label="Decline call"
          >
            <PhoneOff className="h-7 w-7" />
          </button>
          <span className="text-xs text-white/50">Decline</span>
        </div>

        {/* Answer */}
        <div className="flex flex-col items-center gap-2">
          <button
            onClick={handleAnswer}
            className="h-[72px] w-[72px] bg-green-600 hover:bg-green-500 active:bg-green-700 text-white rounded-full flex items-center justify-center shadow-lg transition-colors"
            aria-label="Answer call"
          >
            <Phone className="h-7 w-7" />
          </button>
          <span className="text-xs text-white/50">Answer</span>
        </div>
      </div>
    </div>
  );
}
